import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import PushNotificationService from "services/PushNotificationService";
import { TOPUP_CONFIRMED_TYPE, refreshAfterTopupConfirmed } from "constants/BankReconciliationConstant";

// Sent by public/firebase-messaging-sw.js when an OS notification is clicked
const CLICK_MESSAGE_TYPE = "PUSH_NOTIFICATION_CLICK";

const readPermission = () => (typeof Notification === "undefined" ? "unsupported" : Notification.permission);

const usePushNotifications = (userCode) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(readPermission);

  useEffect(() => {
    let active = true;
    PushNotificationService.isSupported().then((result) => {
      if (active) {
        setSupported(result);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // Registers the token with the backend on every load while permission is granted
  useEffect(() => {
    if (!supported || !userCode || permission !== "granted") {
      return;
    }
    PushNotificationService.enable().catch((err) => console.error("Push registration failed:", err));
  }, [supported, userCode, permission]);

  // Tab visible: the WebSocket already plays the sound, so only refresh the lists in case the socket dropped
  useEffect(() => {
    if (!supported || permission !== "granted") {
      return undefined;
    }
    return PushNotificationService.onForegroundMessage((payload) => {
      queryClient.invalidateQueries({ queryKey: ["get_notification_unread_count"] });
      queryClient.invalidateQueries({ queryKey: ["get_all_notifications"] });
      if (payload?.data?.type === TOPUP_CONFIRMED_TYPE) {
        refreshAfterTopupConfirmed(queryClient);
      }
    });
  }, [supported, permission, queryClient]);

  useEffect(() => {
    if (!supported) {
      return undefined;
    }
    const handleWorkerMessage = (event) => {
      if (event.data?.type === CLICK_MESSAGE_TYPE && event.data.url) {
        history.push(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", handleWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener("message", handleWorkerMessage);
  }, [supported, history]);

  // Must run from a click: Chrome only shows the permission prompt after a user gesture
  const enable = useCallback(async () => {
    const result = await Notification.requestPermission();
    setPermission(result);
  }, []);

  return { supported, permission, enable };
};

export default usePushNotifications;
